/**
 * feat-241 — client for the station-readiness endpoint
 * (`restaurant-backend/routes/curriculum_coverage.py`).
 *
 *   GET /api/curriculum-coverage/{tenant_id}?location_id=<optional>
 *
 * ONE endpoint, not two (P12). The matrix response carries everything the
 * passport needs; the drill-down at `/restaurant/curriculum-coverage/:serverId`
 * performs NO second fetch and runs no second evaluator path. Two endpoints would
 * be two places for "is this track locked" to be answered differently — the exact
 * divergence `TrackCatalog` exists to prevent.
 *
 * Shape notes that are contract, not convenience:
 *
 *  - `cells` is DENSE and ordered identically to `tracks` for every crew member —
 *    one cell per column, always. There is deliberately no reconciliation logic
 *    here for a sparse map: if a cell is ever missing that is a BACKEND DEFECT to
 *    report, not to paper over. Defensive reconciliation would hide it.
 *  - `lessons_total` is on the TRACK object and nowhere else (P14). It is
 *    identical for every crew member, and a second copy is a second thing to
 *    drift. Read the denominator from the track.
 *  - `blocking_track_ids` are IDS, not sentences (P11). The web names those
 *    tracks from the `tracks` titles it already renders as column headers. Never
 *    compose lock copy from anything else, and do not go looking for
 *    `lock_reason.text` — that is composed in the tenant's CONTENT language and
 *    would put Spanish lock copy in an English manager UI.
 *
 * Fake mode (`config.environment === 'fake'`, npm run start:fake): returns a
 * deterministic roster in the same shape so the whole screen renders with no
 * backend. The contract is the real contract — no reshaping.
 */
import { config } from '../config/environment';
import { authHeader } from './authToken';
import { authedApiFetch } from './tokenRefresh';
import type { CellStatus } from '../constants/curriculumCoverage';

// ---------- Domain types (mirror the backend response models) ----------

/** One column of the matrix. `lessons_total` lives HERE and nowhere else (P14). */
export interface CoverageTrack {
  id: string;
  title: string;
  sort_order: number;
  is_default: boolean;
  /** Count of ACTIVE lessons in this track. The `M` of `N of M`. */
  lessons_total: number;
  /** In-progress art. */
  image_s3_key?: string | null;
  /** D10 completed art. */
  completed_image_s3_key?: string | null;
}

/**
 * One (crew, track) pair. feat-302 relaxes P14: a cell MAY carry its own
 * `lessons_total` (this person's applicable lessons in the track); otherwise the
 * column's `track.lessons_total` is the denominator.
 *
 * `blocking_track_ids` is non-empty ONLY when `status === 'locked'`, and every id
 * in it appears in the response's own `tracks` list (backend AC8), which is what
 * makes "a lock reason can never name a track absent from the matrix" structural
 * rather than a client-side hope.
 */
export interface CoverageCell {
  track_id: string;
  status: CellStatus;
  lessons_complete: number;
  next_lesson_title?: string | null;
  blocking_track_ids: string[];
  /**
   * feat-302 (ANSWERS-BACKEND Q5) — false when this track is off-curriculum for
   * this crew member (audience scope). Such a cell never counts toward
   * `tracks_complete` on the backend, and the web excludes it from every
   * client-side count, blocker list and sort key. Absent ⇒ true (an older
   * backend, or an unscoped tenant). Read it through {@link cellApplies}.
   */
  applies?: boolean;
  /**
   * feat-302 (FIXES-R1 F1) — this crew member's own lesson count for the track,
   * when audience scope makes it differ from the column's. Optional and nullable:
   * absent/null ⇒ use `track.lessons_total` (an older backend). Read it through
   * `cellLessonsTotal` in curriculumCoverage/coverageApplies.ts.
   */
  lessons_total?: number | null;
}

export interface CoverageCrewMember {
  server_id: string;
  name: string;
  location_id: string | null;
  /** `""` when the location row is absent — a dangling FK never hides crew (P13). */
  location_name: string;
  /** Count of cells with `status === 'completed'`. */
  tracks_complete: number;
  /** Dense, and in the same order as `tracks`. */
  cells: CoverageCell[];
}

export interface CurriculumCoverageResponse {
  tracks: CoverageTrack[];
  crew: CoverageCrewMember[];
}

// ---------- HTTP plumbing (mirrors trainingProgressService) ----------

function backendUrl(path: string): string {
  const protocol =
    config.environment === 'fake' || config.environment === 'local' ? 'http' : 'https';
  return `${protocol}://${config.restaurantBackendHost}${path}`;
}

/** Raised when the tenant is unreachable (fail-closed — no existence leak). */
export class CurriculumCoverageForbiddenError extends Error {
  constructor() {
    super('Curriculum coverage is not available for this tenant');
    this.name = 'CurriculumCoverageForbiddenError';
  }
}

async function api<T>(path: string): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...authHeader(),
  };
  const resp = await authedApiFetch(backendUrl(path), { method: 'GET', headers });
  if (resp.status === 404 || resp.status === 403) {
    // Fail-closed tenant scoping: "not available", never "does not exist".
    throw new CurriculumCoverageForbiddenError();
  }
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`API ${path} ${resp.status}: ${text}`);
  }
  return resp.json();
}

const isFake = (): boolean => config.environment === 'fake';

export interface CurriculumCoverageParams {
  locationId?: string;
}

export async function getCurriculumCoverage(
  tenantId: string,
  params: CurriculumCoverageParams = {},
): Promise<CurriculumCoverageResponse> {
  if (isFake()) {
    return fakeResponse(params.locationId);
  }
  const qs = new URLSearchParams();
  if (params.locationId) qs.set('location_id', params.locationId);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return api<CurriculumCoverageResponse>(
    `/api/curriculum-coverage/${encodeURIComponent(tenantId)}${suffix}`,
  );
}

// ---------- Fake-mode deterministic data ----------
//
// Six tracks and six crew, chosen to exercise every state the matrix can render:
// a completed row, an in-progress cell with a real fraction, an open cell, a
// single-blocker lock, a multi-blocker lock (the `+N` truncation), a track with
// no art at all (the monogram fallback), and a crew member whose location row is
// missing (P13 — blank location name, still present).

const FAKE_TRACKS: CoverageTrack[] = [
  {
    id: 'trk-basics',
    title: 'Floor Basics',
    sort_order: 1,
    is_default: true,
    lessons_total: 4,
    image_s3_key: null,
    completed_image_s3_key: null,
  },
  {
    id: 'trk-barista',
    title: 'Barista',
    sort_order: 2,
    is_default: false,
    lessons_total: 7,
    image_s3_key: 'track-images/barista.png',
    completed_image_s3_key: 'track-images/barista-done.png',
  },
  {
    id: 'trk-prep',
    title: 'Prep',
    sort_order: 3,
    is_default: false,
    lessons_total: 5,
    image_s3_key: 'track-images/prep.png',
    completed_image_s3_key: null,
  },
  {
    id: 'trk-expo',
    title: 'Expo',
    sort_order: 4,
    is_default: false,
    lessons_total: 6,
    image_s3_key: null,
    completed_image_s3_key: null,
  },
  {
    id: 'trk-closing',
    title: 'Closing',
    sort_order: 5,
    is_default: false,
    lessons_total: 3,
    image_s3_key: null,
    completed_image_s3_key: null,
  },
  {
    id: 'trk-lead',
    title: 'Shift Lead',
    sort_order: 6,
    is_default: false,
    lessons_total: 8,
    image_s3_key: null,
    completed_image_s3_key: null,
  },
];

const done = (id: string, n: number): CoverageCell => ({
  track_id: id,
  status: 'completed',
  lessons_complete: n,
  next_lesson_title: null,
  blocking_track_ids: [],
});
const doing = (id: string, n: number, next: string): CoverageCell => ({
  track_id: id,
  status: 'in_progress',
  lessons_complete: n,
  next_lesson_title: next,
  blocking_track_ids: [],
});
const open = (id: string): CoverageCell => ({
  track_id: id,
  status: 'available',
  lessons_complete: 0,
  next_lesson_title: null,
  blocking_track_ids: [],
});
/** feat-302 — a track off-curriculum for this crew member. */
const notAssigned = (id: string): CoverageCell => ({
  track_id: id,
  status: 'available',
  lessons_complete: 0,
  next_lesson_title: null,
  blocking_track_ids: [],
  applies: false,
});
const locked = (id: string, blockers: string[]): CoverageCell => ({
  track_id: id,
  status: 'locked',
  lessons_complete: 0,
  next_lesson_title: null,
  blocking_track_ids: blockers,
});

const FAKE_CREW: CoverageCrewMember[] = [
  {
    server_id: 'srv-ana',
    name: 'Ana Ruiz',
    location_id: 'loc-sf',
    location_name: 'Ladle & Leaf SF',
    tracks_complete: 3,
    cells: [
      done('trk-basics', 4),
      done('trk-barista', 7),
      done('trk-prep', 5),
      doing('trk-expo', 2, 'Reading the pass'),
      open('trk-closing'),
      locked('trk-lead', ['trk-expo', 'trk-closing']),
    ],
  },
  {
    server_id: 'srv-carlos',
    name: 'Carlos Mendez',
    location_id: 'loc-sf',
    location_name: 'Ladle & Leaf SF',
    tracks_complete: 1,
    cells: [
      done('trk-basics', 4),
      doing('trk-barista', 3, 'Steaming milk'),
      open('trk-prep'),
      open('trk-expo'),
      open('trk-closing'),
      locked('trk-lead', ['trk-barista', 'trk-prep', 'trk-expo', 'trk-closing']),
    ],
  },
  {
    server_id: 'srv-dee',
    name: 'Dee Okafor',
    location_id: 'loc-oak',
    location_name: 'Ladle & Leaf Oakland',
    tracks_complete: 6,
    cells: [
      done('trk-basics', 4),
      done('trk-barista', 7),
      done('trk-prep', 5),
      done('trk-expo', 6),
      done('trk-closing', 3),
      done('trk-lead', 8),
    ],
  },
  {
    server_id: 'srv-priya',
    name: 'Priya Nair',
    location_id: 'loc-oak',
    location_name: 'Ladle & Leaf Oakland',
    tracks_complete: 1,
    cells: [
      done('trk-basics', 4),
      doing('trk-barista', 6, 'Latte art'),
      doing('trk-prep', 1, 'Knife safety'),
      open('trk-expo'),
      locked('trk-closing', ['trk-expo']),
      locked('trk-lead', ['trk-expo', 'trk-closing']),
    ],
  },
  {
    server_id: 'srv-tom',
    name: 'Tom Alvarez',
    // P13: a dangling location_id still appears, with a blank location name.
    location_id: 'loc-gone',
    location_name: '',
    tracks_complete: 0,
    cells: [
      doing('trk-basics', 1, 'Greeting the table'),
      open('trk-barista'),
      open('trk-prep'),
      open('trk-expo'),
      open('trk-closing'),
      locked('trk-lead', ['trk-basics']),
    ],
  },
  {
    server_id: 'srv-blake',
    name: 'Blake Osei',
    location_id: 'loc-sf',
    location_name: 'Ladle & Leaf SF',
    tracks_complete: 0,
    cells: [
      open('trk-basics'),
      open('trk-barista'),
      open('trk-prep'),
      open('trk-expo'),
      open('trk-closing'),
      // feat-302: Shift Lead is scoped away from Blake — renders "—".
      notAssigned('trk-lead'),
    ],
  },
];

function fakeResponse(locationId?: string): CurriculumCoverageResponse {
  const crew = locationId
    ? FAKE_CREW.filter((c) => c.location_id === locationId)
    : FAKE_CREW;
  return { tracks: FAKE_TRACKS, crew };
}
